import { NgForm } from '@angular/forms';
import { IAddressData } from '@df/session/api/address-book/address';
import { Subject, Subscription } from 'rxjs';
// import { DeliveryDate } from './../../module/delivery-date/delivery-date.interfaces';
import { InjectionToken } from '@angular/core';
import { IRequestCallbackFunction, IResponseMetaResult } from '@df/api/request/request.service';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { CreditCard } from '@df/session/api/credit-cards/credit-card';
import { Nexus } from '@tomandco/nexus-api-types';

/**
 * @deprecated Use Nexus instead
 */
export namespace Checkout {
    export enum EVENTS {
        StepChange = 'checkout.step.change',
        SubmitBefore = 'checkout.submit.before',
        SubmitSuccess = 'checkout.submit.success',
        SuccessTracking = 'checkout.submit.success-tracking',
        SubmitError = 'checkout.submit.error',
        Update = 'checkout.update',
        Init = 'checkout.init',
        Create = 'checkout.create',
        InitAfter = 'checkout.init.after',
        Destroy = 'checkout.destroy',
        ShippingMethodsUpdate = 'checkout.shippingMethods.update',
        PaymentMethodsUpdate = 'checkout.paymentMethods.update',
        DeliveryDateUpdate = 'checkout.deliveryDate.update',
        TotalsUpdate = 'checkout.totals.update',
        StepsSetUpdate = 'checkout.stepsSet.update',
        StepEntered = 'checkout.stepEntered',
        StepValidityChange = 'checkout.validityChange',
        SuccessLoadError = 'checkoutSuccess.load.error',
        PaymentTokensBefore = 'checkout.paymentTokens.before',
        PickPaymentMethod = 'checkout.pickPaymentMethod',
        PickShippingMethod = 'checkout.pickShippingMethod',
        SavePaymentMethodBefore = 'checkout.savePaymentMethod.before',
        SaveShippingMethodBefore = 'checkout.saveShippingMethod.before',
        CheckoutGuestStateChanged = 'checkout.guestStateChanged',
        RefreshPaymentMethods = 'checkout.refreshPaymentMethods',
        SetPaymentMethodsBefore = 'checkout.setPaymentMethods.before'
    }

    export enum Endpoints {
        ResetShippingMethod = 'checkout/resetShippingMethod'
    }

    export enum Errors {
        RedirectCart = 'REDIRECT_CART'
    }

    export enum EStepName {
        account = 'account',
        delivery = 'delivery',
        deliveryDate = 'deliveryDate',
        payment = 'payment',
        confirmation = 'confirmation'
    }

    export const GENERAL_PAYMENT_ERROR_MESSAGE = 'Please try again with another form of payment.';

    export const CHECKOUT_CONFIRMATION_STEP_NAME = EStepName.confirmation;

    export interface ILoadResponse {
        checkout?: IData;
    }

    export interface IData extends Nexus.Api.Checkout.Load.CheckoutResponse {
        // delivery_date?: {
        //     config: any;
        //     value?: string;
        // },
        shipping: IShippingData;
        totals: Nexus.Lib.Basket.Totals;
        user?: {
            email: string;
            exists: boolean;
            name?: string;
            newsletter?: boolean;
        };
        //delivery_date?: DeliveryDate.IBackendData;
    }

    export interface IShippingData {
        methods: IShippingMethod[];
        message?: string;
    }

    export interface IQuoteData extends IQuoteDataPartial {
        address: {
            billing: IBillingAddress;
            shipping: IAddress;
        };
        cart_id: string;
        isVirtual?: boolean;
        quote_id: number | string;
        // source?: string;
    }

    export interface IQuoteDataPartial {
        /**
         * Addresses
         */
        address: {
            billing: any;
            shipping: any;
        };
        /**
         * Newsletter subscription
         */
        newsletter?: boolean;

        /**
         * Newsletter subscription source id
         */
        source?: string;
        /**
         * Shipping method data
         */
        shipping: IQuoteDataShipping;
        /**
         * Payment method data
         */
        payment: IQuoteDataPayment;
        /**
         * Account data
         */
        account?: IQuoteDataAccount;
        gift: any;
        multishipping: boolean;
        billing: {
            to_shipping: boolean;
        };
        custom?: IQuoteDataCustom;

        /**
         * This is standard method to pass any custom subscriptions
         * like 3rd party optins, catalogue optins, post optins.
         * This will by default work with TCO_CoreApiNewsletter
         */
        subscriberProperties?: { [key: string]: boolean };
    }

    export interface IQuoteDataAccount {
        email?: string;
    }

    export interface IQuoteDataShipping {
        method?: string;
        additionalData?: IQuoteDataShippingAdditionalData;
        delivery_date?: string | boolean;
    }

    export interface IQuoteDataShippingAdditionalData {
        store_id?: number | null; // storepickup store
        booking_code?: string | null; // storepickup booking code
    }

    export interface IQuoteDataPayment {
        method?: string | number;
        additional_data?: any;
    }

    export interface IQuoteDataCustom {
        gift?: IGiftMessageData;
        delivery_note?: string;
    }

    export interface ISubmissionData {
        quote_id: number | string;
        billing_address?: IAddress;
        shipping_address?: IAddress;
        email?: string;
        newsletter?: boolean;
        source?: string;
        paymentMethod: ISubmissionPaymentData;
        custom?: any;
        subscriberProperties?: any;
    }

    export interface ISubmissionPaymentData {
        method?: string | number;
        additional_data?: any;
    }

    export interface ISubmissionPaymentVaultData {
        method: 'vault';
        additional_data: ISubmissionPaymentVaultAdditionalData;
    }

    export interface ISubmissionPaymentVaultAdditionalData {
        public_hash: string;
        cvc?: string;
    }

    export interface IGiftMessageData {
        message?: string;
    }

    export interface IShippingMethod {
        code: string;
        description?: string;
        price: number;
        title: string;
        lead_time?: string;
    }

    export interface IPaymentMethod {
        /**
         * Method code
         */
        code: string | number;
        /**
         * Method title
         */
        title: string;
        /**
         * Some payment methods are intended to be used in embedded iframe, this flag is true then
         */
        inFrame?: boolean;
    }

    export interface ICardPaymentMethod extends IPaymentMethod {
        isCard: true;
    }

    export interface IAddress extends IAddressData {
        customer_address_id?: number;
    }

    export interface IBillingAddress extends IAddress {
        email?: string;
    }

    export interface ISubmitEventData {
        request: ISubmissionData | undefined;
        basket: Nexus.Lib.Basket.BasketData;
    }

    export interface ISubmitSuccessEventData extends ISubmitEventData {
        response: ISubmissionResponse;
    }

    export interface IDataError {
        [key: string]: string;
    }

    export interface ISubmitErrorEventData extends ISubmitEventData {
        error: any;
        data_error?: IDataError;
    }

    export interface ISubmissionResponse extends IResponseMetaResult {
        result?: ISubmissionResponseResult;
        preventSuccess?: boolean;
    }

    export interface ISubmissionResponseResult {
        order: {
            number?: string;
            numbers?: string;
        };
    }

    /**
     * Used with checkout next state
     * constains information about what changed
     */
    export interface IStateChange {
        full: boolean;
        patch?: any;
    }

    export interface ISuccessResponse extends IResponseMetaResult {
        success: ISuccessData;
        error?: string;
    }

    export interface ISuccessData {
        number?: string;
        numbers?: string[];
        customer: ISuccessCustomerData;
        /**
         * True first time you see checkout success page, false later
         * Protection against double tracking
         * @type {boolean}
         */
        tracking: boolean;
        total: number;
        qty: number;
        items?: ISuccessItemData[];
        currency?: string;
        date?: string;
    }

    export interface ISuccessCustomerData {
        // customer id from order
        id?: number;
        // customer firstname from billing address
        firstname: string;
        // customer lastname from billing address
        lastname: string;
        // customer email from order
        email: string;
        // if user can create account BE flag (may already exist)
        can_register: boolean;
        // if user subscribed to newsletter
        newsletter?: boolean;
    }

    export interface ISuccessItemData {
        product_id: number;
        children?: ISuccessItemData[];
        qty: number;
        name: string;
        request: Nexus.Lib.Basket.BasketRequestData;
    }

    export interface ISteps {
        [name: string]: IStep;
    }

    export interface IStep {
        name: EStepName;
        stepTitle: string;
        order: number;
        valid: boolean;
        hidden?: boolean;
        isCurrent: boolean;
        currentStep: IStep;
        domElement: HTMLElement;
        goTo: () => void;
        markSubmitted: (scroll?: boolean) => void;
        markUnsubmitted: () => void;
        isConfirmation?: boolean;
        forms: NgForm[];
    }

    export interface IStepChangeData {
        checkout: ICheckoutFormComponent;
        stepBeforeChange: string;
    }

    export interface IStepValidityChangeData {
        step: IStep;
    }

    export interface ISubmitBeforeEventData {
        continueSubmission: boolean;
        checkout: ICheckoutFormComponent;
        data: ISubmissionData;
        checkoutData: Checkout.IQuoteData;
        next(): Promise<any>;
        onSuccess: IRequestCallbackFunction[];
    }

    export interface IStepsSetUpdateEventData {
        steps: ISteps;
        checkout: ICheckoutFormComponent;
    }

    export interface IInitEventData {
        checkout: ICheckoutFormComponent;
        currentStep: IStep;
        currentStepName: string;
    }
    export interface IDestroyEventData {
        checkout: ICheckoutFormComponent;
    }
    export interface ICheckoutGuestStateChangedEventData {
        toState: boolean;
        checkout: ICheckoutFormComponent;
    }
    export interface IStepChangeEventData {
        stepName: EStepName;
        step: IStep;
        checkout: ICheckoutFormComponent;
        stepBeforeChange?: EStepName;
    }

    export interface ICreditCardData {
        holderName?: string;
        number?: string;
        type?: string;
        expiryMonth?: number | string;
        expiryYear?: number | string;
        cvc?: string;
        store_cc?: boolean;
        issueNumber?: string;
    }

    export interface IPaymentTokensBeforeEvent {
        tokens: CreditCard[];
        methods: IPaymentMethod[];
    }

    export interface ISaveBillingAddressData {
        email: string;
        billing_to_shipping: boolean;
        billing_address: IAddressData;
    }

    export interface ICheckoutPaymentMethodsComponent {
        data: IQuoteDataPayment;
        status: Status;
        noPaymentNeeded: boolean;
        methods: IPaymentMethod[];
        getMethod(code: string | number | undefined): IPaymentMethod | undefined;
    }
    export interface ICheckoutShippingMethodsComponent {
        stepForm: NgForm;
        status: Status;
        data: IQuoteDataShipping;
        noShippingNeeded: boolean;
        allMethods: IShippingMethod[];
        collectionMethods: IShippingMethod[];
        methods: IShippingMethod[];
        getMethod(code: string | undefined): IShippingMethod | undefined;
    }

    export interface ICheckoutFormComponent {
        backendData: IData;
        data: IQuoteData;
        subscribe(callback: (data: IStateChange) => void): Subscription;
    }

    export interface ISavePaymentMethodData extends IQuoteDataPayment {
        ver: 2;
    }

    export interface ISavePaymentMethodEventData extends ISavePaymentMethodData {
        methodDetails?: IPaymentMethod;
    }

    export interface ISaveShippingMethodData extends IQuoteDataShipping {}

    export interface ISaveShippingMethodEventData extends ISaveShippingMethodData {
        methodDetails?: IShippingMethod;
    }

    export const CheckoutFormPaymentComponentToken = new InjectionToken<ICheckoutForPaymentComponent>('checkoutForPaymentComponent');

    export interface ICheckoutForPaymentComponent {
        submittedSubject?: Subject<boolean>;
        forms?: NgForm[];
        status: Status;
        paymentMethodCode: string | number | undefined;
        paymentMethodByCode(code: string | number): IPaymentMethod | undefined;
        redirectToSuccess(): void;
        getSubmissionData(): ISubmissionData;
        submitWithData(
            data: ISubmissionData,
            onSuccess?: IRequestCallbackFunction,
            onError?: IRequestCallbackFunction,
            force?: boolean
        ): void;
        stepsSequence?: IStep[];
        billingAddress: IAddressData | undefined;
        shippingAddress: IAddressData | undefined;
        email: string | undefined;
        isCheckoutValid: boolean;
        validateCheckout(): boolean;
        isVirtual: boolean;
        paymentMethods: IPaymentMethod[];
        app: App;
        isFree: boolean;
        dataError?: IDataError;
        shippingMethod: IShippingMethod | undefined;
    }
}
